import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
// Removed config import to avoid production issues
import SEOHead from "../components/SEOHead";
import { 
  Lightbulb, 
  Settings, 
  Users, 
  BookOpen, 
  Cpu, 
  Brain,
  ArrowRight,
  CheckCircle,
  Star,
  Target,
  Rocket,
  Clock
} from "lucide-react";

const Services = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // SEO Configuration
  const seoConfig = {
    title: "KI-Services & AI-Beratung | Komplettes Leistungsangebot | BROVKO",
    description: "Komplettes KI-Services Portfolio: Nachhaltige Transformation, C-Level Coaching, Private GPT-Modelle, KI-Plattformen und Enterprise AI-Lösungen. Jetzt Services entdecken!",
    keywords: "KI-Services, AI-Beratung Portfolio, Enterprise KI-Lösungen, KI-Akademie, Private GPT-Modelle, KI-Plattform, Strategische KI-Beratung, Nachhaltige KI-Transformation",
    canonicalUrl: "/leistungsangebot",
    ogTitle: "KI-Services & AI-Beratung | Komplettes Leistungsangebot für Unternehmen",
    ogDescription: "Entdecken Sie unser komplettes KI-Services Portfolio: Von nachhaltiger Transformation bis zu Private GPT-Modellen. Alle AI-Beratungsleistungen im Überblick.",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "BROVKO KI-Services Portfolio",
      "description": "Komplettes KI-Services Angebot für Unternehmen: Transformation, Coaching, Plattformen und Enterprise AI-Lösungen",
      "provider": {
        "@type": "Organization",
        "name": "BROVKO"
      },
      "areaServed": ["DE", "AT", "CH"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "KI-Services Portfolio",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Nachhaltige KI-Transformation",
              "description": "Ganzheitliche KI-Transformation für nachhaltigen Unternehmenserfolg"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "KI-Akademie",
              "description": "C-Level Coaching und Executive KI-Training für Führungskräfte"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Private GPT-Modelle für Unternehmen",
              "description": "DSGVO-konforme AI-Systeme und Private GPT-Implementierung"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "KI-MVP-Entwicklung",
              "description": "Funktionierendes KI-MVP in 3-4 Wochen zum Festpreis von 19.000 EUR"
            },
            "price": "19000",
            "priceCurrency": "EUR"
          }
        ]
      }
    }
  };

  // Modal handlers
  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const services = [
    {
      icon: Target,
      title: t('services.transformation.title'),
      description: t('services.transformation.description'),
      features: [
        t('services.transformation.feature1'),
        t('services.transformation.feature2'),
        t('services.transformation.feature3'),
        t('services.transformation.feature4')
      ],
      link: "/leistungsangebot/ki-transformation",
      color: "bg-indigo-500"
    },
    {
      icon: Lightbulb,
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      features: [
        t('services.consulting.feature1'),
        t('services.consulting.feature2'),
        t('services.consulting.feature3'),
        t('services.consulting.feature4')
      ],
      link: "/leistungsangebot/ki-akademie",
      color: "bg-blue-500"
    },
    {
      icon: Users,
      title: t('services.coaching.title'),
      description: t('services.coaching.description'),
      features: [
        t('services.coaching.feature1'),
        t('services.coaching.feature2'),
        t('services.coaching.feature3'),
        t('services.coaching.feature4')
      ],
      link: "/leistungsangebot/ki-coaching",
      color: "bg-purple-500"
    },
    {
      icon: Rocket,
      title: t('services.mvp.title'),
      description: t('services.mvp.description'),
      features: [
        t('services.mvp.feature1'),
        t('services.mvp.feature2'),
        t('services.mvp.feature3'),
        t('services.mvp.feature4')
      ],
      link: "/leistungsangebot/ki-mvp",
      color: "bg-orange-500",
      highlight: true,
      price: "19.000 EUR",
      timeline: "3-4 Wochen"
    },
    {
      icon: Settings,
      title: t('services.kilab.title'),
      description: t('services.kilab.description'),
      features: [
        t('services.kilab.feature1'),
        t('services.kilab.feature2'),
        t('services.kilab.feature3'),
        t('services.kilab.feature4')
      ],
      link: "/leistungsangebot/ki-lab",
      color: "bg-red-500",
      highlight: true,
      timeline: "Sofort verfügbar"
    },
    {
      icon: BookOpen,
      title: t('services.workshops.title'),
      description: t('services.workshops.description'),
      features: [
        t('services.workshops.feature1'),
        t('services.workshops.feature2'),
        t('services.workshops.feature3'),
        t('services.workshops.feature4')
      ],
      link: "/leistungsangebot/ki-workshops",
      color: "bg-green-500"
    },
    {
      icon: Brain,
      title: t('services.platform.title'),
      description: t('services.platform.description'),
      features: [
        t('services.platform.feature1'),
        t('services.platform.feature2'),
        t('services.platform.feature3'),
        t('services.platform.feature4')
      ],
      link: "/leistungsangebot/ki-plattform",
      color: "bg-purple-500"
    },
    {
      icon: Cpu,
      title: t('services.applications.title'),
      description: t('services.applications.description'),
      features: [
        t('services.applications.feature1'),
        t('services.applications.feature2'),
        t('services.applications.feature3'),
        t('services.applications.feature4')
      ],
      link: "/leistungsangebot/ki-anwendungen",
      color: "bg-brand"
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('services.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-ink-muted max-w-4xl mx-auto mb-8">
              {t('services.hero.subtitle')}
            </p>
            <div className="flex items-center justify-center gap-4 text-lg">
              <Star className="h-6 w-6 text-yellow-400" />
              <span>{t('services.hero.experience')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-16">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
              {t('services.overview.title')}
            </h2>
            <p className="text-xl text-ink-muted max-w-[62ch]">
              {t('services.overview.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className={`group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${
                  service.highlight ? 'ring-2 ring-orange-500 relative overflow-hidden' : ''
                }`}>
                  {service.highlight && (
                    <div className="absolute top-0 right-0 bg-orange-400 text-orange-950 px-4 py-1 text-sm font-bold">
                      {t('services.mvp.highlight')}
                    </div>
                  )}
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-4 rounded-full ${service.color}`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink">
                          {service.title}
                        </h3>
                        {service.price && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-lg font-bold text-orange-400">{service.price}</span>
                            {service.timeline && (
                              <>
                                <span className="text-ink-faint">•</span>
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4 text-ink-faint" />
                                  <span className="text-sm text-ink-muted">{service.timeline}</span>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-ink-muted mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-brand-400" />
                          <span className="text-sm text-ink-muted">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to={service.link}>
                      <Button className={`w-full transition-colors ${
                        service.highlight && service.link.includes('mvp')
                          ? 'bg-orange-400 hover:bg-orange-300 text-orange-950 group-hover:bg-orange-300' 
                          : service.highlight && service.link.includes('ki-lab')
                          ? 'bg-red-600 hover:bg-red-700 text-white group-hover:bg-red-700'
                          : 'bg-brand hover:bg-brand-600 text-ink-onBrand group-hover:bg-brand-600'
                      }`}>
                        {service.highlight && service.link.includes('mvp') 
                          ? t('services.mvp.cta') 
                          : t('services.learnMore')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-16">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
              {t('services.why.title')}
            </h2>
            <p className="text-xl text-ink-muted max-w-[62ch]">
              {t('services.why.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-brand/15 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Star className="h-10 w-10 text-brand-400" />
              </div>
              <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                {t('services.why.expertise.title')}
              </h3>
              <p className="text-ink-muted">
                {t('services.why.expertise.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-500/15 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Settings className="h-10 w-10 text-blue-400" />
              </div>
              <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                {t('services.why.practical.title')}
              </h3>
              <p className="text-ink-muted">
                {t('services.why.practical.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-500/15 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-400" />
              </div>
              <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                {t('services.why.results.title')}
              </h3>
              <p className="text-ink-muted">
                {t('services.why.results.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-500/15 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-10 w-10 text-purple-400" />
              </div>
              <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                {t('services.why.support.title')}
              </h3>
              <p className="text-ink-muted">
                {t('services.why.support.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
            {t('services.cta.title')}
          </h2>
          <p className="text-xl text-brand-300 max-w-[62ch] mb-8">
            {t('services.cta.subtitle')}
          </p>
          <Button 
            size="lg" 
            className="bg-surface text-brand-400 hover:bg-surface-raised px-8 py-4 text-lg font-semibold"
            onClick={openContactModal}
          >
            {t('services.cta.button')}
          </Button>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="Lösungen"
        buttonContext="CTA Section"
        buttonText="Kostenloses Erstgespräch buchen"
      />
    </div>
  );
};

export default Services;